import termToLowerCasePreservingAcronyms from '../../term/termToLowerCasePreservingAcronyms'
import { Location } from '../../../types/models/Location.model'
import { isLocationCity } from '../../location/useCases/isLocationCity'
import { isFacetPageType } from '@/domain/useCases/pageTypeCheck'
import { PageType } from '@/types/models/PageType.model'

interface GeneratePageDescriptionParams {
  pageType: PageType
  title: string
  term: string
  customDescription?: string
  count?: number
  location: Location
  shouldDefaultRegionalize?: boolean
}

interface GeneratePageDescription {
  (params: GeneratePageDescriptionParams): string
}

const generatePageDescription: GeneratePageDescription = ({
  pageType,
  title,
  term,
  customDescription,
  count,
  location,
  shouldDefaultRegionalize = false
}) => {
  const lowercaseTitle = termToLowerCasePreservingAcronyms(title)
  const lowercaseTerm = termToLowerCasePreservingAcronyms(term)
  const locationWithParent = isLocationCity(location)
    ? `${title}, ${location?.parentLocation}`
    : title

  const unitedStatesLocationFromAddressParts = location?.address_parts?.filter(
    (addressPart) => {
      return addressPart.title?.includes('United States')
    }
  )

  const isUSLocationFromAddressParts =
    unitedStatesLocationFromAddressParts &&
    unitedStatesLocationFromAddressParts?.length > 0

  const descriptions = {
    Location: isUSLocationFromAddressParts
      ? `Find top drug and alcohol rehabs in ${locationWithParent}. Your insurance may cover costs for substance use disorder treatments, including outpatient, residential, and detox.`
      : `Find top drug and alcohol rehabs in ${locationWithParent}. Review ${
          shouldDefaultRegionalize ? 'centres' : 'centers'
        } offering substance use disorder treatments, including outpatient, residential, and detox.`,
    setting: `Compare the top drug and mental health rehab centers offering ${lowercaseTitle}. Handpicked, offering the best treatment in the world. Pricing info, reviews, and more.`,
    treatment_therapy: `Compare the top drug and mental health rehab centers offering ${lowercaseTitle}. Handpicked, offering the best treatment in the world. Pricing info, reviews, and more.`,
    special_consideration: `Compare the top drug and mental health rehab centers offering ${lowercaseTitle}. Handpicked, offering the best treatment in the world. Pricing info, reviews, and more.`,
    treatment_method: `Compare the top ${lowercaseTitle} drug and mental health rehab centers. Handpicked, offering the best treatment in the world. Pricing info, reviews, and more.`,
    amenities: `Compare the top drug and mental health rehab centers offering ${lowercaseTitle}. Handpicked, offering the best treatment in the world. Pricing info, reviews, and more.`,
    activity: `Compare the top drug and mental health rehab centers offering ${lowercaseTitle}. Handpicked, offering the best treatment in the world. Pricing info, reviews, and more.`,
    who_we_treat: `Compare the top drug and mental health rehab centers for ${lowercaseTitle}. Handpicked, offering the best treatment in the world. Pricing info, reviews, and more.`,
    what_we_treat: `Our independent research team evaluated the ${count} best treatment centers for ${lowercaseTitle}. Review our curated list of centers offering care options such as virtual, outpatient, and residential treatment. Read unbiased reviews to find the best treatment center for your recovery needs.`,
    treatment_services: `Compare the top ${lowercaseTitle} drug and mental health rehab centers. Handpicked, offering the best treatment in the world. Pricing info, reviews, and more.`,
    price: `Compare the top drug and mental health rehab centers offering ${lowercaseTitle}. Handpicked, offering the best treatment in the world. Pricing info, reviews, and more.`,
    insurance_accepted: `Compare the top drug and mental health rehab centers accepting insurance. Handpicked, offering the best treatment in the world. Pricing info, reviews, and more.`,
    accepted_insurance: `Browse our collection of ${count} treatment centers accepting ${title}. Handpicked, offering the best treatment in the world. Pricing info, reviews, and more.`,
    'Location+price': `Compare the top ${lowercaseTerm} rehab centers in ${title}. Handpicked, offering the best treatment in the world. Pricing info, reviews, and more.`,
    'Location+what_we_treat': `Our independent research team evaluated the ${count} best treatment centers for ${lowercaseTerm} in ${title}. Review our curated list of centers offering care options such as virtual, outpatient, and residential treatment. Read unbiased reviews to find the best treatment center for your recovery needs.`,
    'Location+special_considerations': `Find the best ${lowercaseTerm} rehab centers in ${title}. Recovery.com helps you find luxury, inpatient, detox and outpatient treatment centers near you. Pricing, reviews, and more.`,
    'Location+who_we_treat': `Find the best ${lowercaseTerm} rehab centers in ${title}. Recovery.com helps you find luxury, inpatient, detox and outpatient treatment centers near you. Pricing, reviews, and more.`,
    'Location+treatment_methods': `Find the best ${lowercaseTerm} rehab centers in ${title}. Recovery.com helps you find luxury, inpatient, detox and outpatient treatment centers near you. Pricing, reviews, and more.`,
    'Location+treatment_therapy': `Find the best ${lowercaseTerm} rehab centers in ${title}. Recovery.com helps you find luxury, inpatient, detox and outpatient treatment centers near you. Pricing, reviews, and more.`,
    'Location+treatment_therapies': `Find the best ${lowercaseTerm} rehab centers in ${title}. Recovery.com helps you find luxury, inpatient, detox and outpatient treatment centers near you. Pricing, reviews, and more.`,
    'Location+setting': `Find the best ${lowercaseTerm} rehab centers in ${title}. Recovery.com helps you find luxury, inpatient, detox and outpatient treatment centers near you. Pricing, reviews, and more.`,
    'Location+treatment_services': `Find the best ${lowercaseTerm} rehab centers in ${title}. Recovery.com helps you find the best treatment options near you. Pricing, reviews, and more.`,
    'Location+aftercare': `Find the best ${lowercaseTerm} rehab centers in ${title}. Recovery.com helps you find the best treatment options near you. Pricing, reviews, and more.`,
    'Location+accepted_insurance': `Find the best rehab centers accepting ${term} in ${title}. Recovery.com helps you find the best treatment options near you. Pricing, reviews, and more.`,
    'Location+insurance_accepted': `Find the best rehab centers accepting ${term} in ${title}. Recovery.com helps you find the best treatment options near you. Pricing, reviews, and more.`,
    'Location+activities': `Find the best ${lowercaseTerm} rehab centers in ${title}. Recovery.com helps you find the best treatment options near you. Pricing, reviews, and more.`
  }

  const termSpecificDescriptions = {
    luxury: {
      amenities: `Explore luxury rehabs with amenities including private rooms, gourmet meals, and tailored care to ensure a personalized, holistic path to recovery.`,
      'Location+amenities': `Explore luxury rehab in ${title} with amenities including private rooms, gourmet meals, and tailored care to ensure a personalized, holistic path to recovery.`
    }
  }

  let descriptionToReturn = descriptions[pageType]

  if (customDescription && !isFacetPageType(pageType)) {
    descriptionToReturn = customDescription
  }

  const termSpecificDescription = (termSpecificDescriptions[
    term.toLowerCase()
  ] || termSpecificDescriptions[title.toLowerCase()])?.[pageType]

  if (termSpecificDescription) {
    descriptionToReturn = termSpecificDescription
  }

  return descriptionToReturn
}

export default generatePageDescription
